const study = {
  meta: {
    slug: "working-capital-franchise-310k-holiday-inventory",
    title:
      "$310K for the holiday season: How a five-location retail franchise financed inventory in three days without a personal guarantee",
    excerpt:
      "A regional shoe-store franchisee had three-and-a-half weeks to stock five locations before Black Friday. Inventory needed to be paid in full at wholesale before it shipped. Here's the illustrative Working Capital LOC structure that closed without a personal guarantee.",
    industry: "Retail · Multi-location franchise",
    product: "Working Capital Line of Credit (revolving)",
    amount: "$310K",
    term: "24 mo revolving / 18 mo repay",
    timeToFund: "3 days",
    state: "Texas",
    challenge: "Seasonal inventory financing",
    image:
      "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-08-02",
    readTime: "5 min read",
    tags: ["working-capital"],
    relatedArticles: [
      "working-capital-vs-line-of-credit",
      "combining-working-capital-and-line-of-credit",
    ],
  },
  situation: `A franchisee running five locations of a national footwear brand across the Dallas–Fort Worth metro. Six years in business, average annual revenue $4.2M across the network, holiday quarter typically representing 38% of full-year revenue.

The franchise required inventory to be paid to the corporate supplier **at time of order**, not on net-30 terms — a common franchise structure that keeps supply chain lean but makes small operators cash-shy in Q4. The Black Friday / Cyber Monday / December run needed **~$285K in inventory** across the five stores; another $25K would cover holiday marketing spend and seasonal payroll.

Cash on hand: $95K, most of which was earmarked for rent and payroll through October. Business credit cards had a combined $80K limit but a 22% APR that would eat margin. The owner needed a **line of credit** — something to draw on, pay back, and redraw next holiday season.

Time available: **26 days** until the first pallet needed to ship.`,
  structure: `**Why a Working Capital Line of Credit, not a term loan.** A term loan gives you the money once and amortizes over 3–5 years. A working capital LOC lets you draw only what you need, pay it back as revenue comes in, and redraw the following season. For a seasonal business, the LOC is the correct product — you pay interest only on what's outstanding, not the full facility amount.

The illustrative fintech LOC that closed the file:

- **$310K revolving line of credit** — draw as needed, up to the limit
- **24-month draw period + 18-month repay** after the last draw
- **~16% APR** on drawn balances, no fee on undrawn availability
- **No personal guarantee** — earned by 6 years of clean bank statements and franchise-network scale (five locations reduces single-point-of-failure risk)
- **Approval based on** trailing 12-month deposits (avg $350K/mo) and franchise-brand strength — the lender maintained a "franchise concept whitelist" for this exact scenario
- **Draws hit the operating account** same-day via ACH, with a 3-day float for wires to the franchisor

**Documents that moved the file in 3 days:**

1. Last 4 months of business bank statements (auto-pulled via secure Plaid connection — no PDF gathering)
2. Signed franchise agreement demonstrating brand permissibility
3. Business tax return (most recent)
4. Personal FICO check (soft pull) — 690, cleared the gate

**What a savvy broker would NOT do:** stack an MCA on top of the LOC to "give more optionality." The LOC alone was sized correctly. Adding a second short-term product would have consumed the underwriting capacity for the next franchise expansion loan the owner was already planning for 2027.`,
  outcome: `Application signed on day 1. Underwriting cleared day 2. First $220K draw hit the operating account **on day 3** — 23 days before the Black Friday inventory ship deadline.

Q4 performance:

- **Holiday quarter revenue up 14% YoY** across all five locations — the extra inventory depth meant fewer stockouts on high-margin Nike releases
- **LOC drawn to $268K peak** in early December, paid down to $92K by end of January, fully paid off by March 15
- **Total interest cost across the draw**: ~$18,400 (~5.9% effective annualized on the drawn balance, since half the balance was outstanding for only two months)
- **LOC facility remains open** with $310K available for Q4 2027 — no reapplication, no re-underwriting

The takeaway: **for seasonal businesses, matching the product to the pattern matters more than chasing the lowest sticker rate.** A working capital LOC with a 16% APR on drawn balances is dramatically cheaper than a 12% term loan the borrower has to service year-round — because the LOC is only "on" when needed.`,
};

export default study;
